import * as React from 'react';
import clsx from 'clsx';
import type { Echo } from '@/types';
import type { PublicChatTheme } from '../themes/types';
import defaultEchoImage from '@/assets/images/elements/default_echo.webp';
import algolympBg from '@/assets/images/elements/algolymp_background.webp';

interface PublicChatHeaderProps {
  echo: Echo;
  theme: PublicChatTheme;
  useLegacyAlgolympBranding?: boolean;
}

export const PublicChatHeader: React.FC<PublicChatHeaderProps> = ({
  echo,
  theme,
  useLegacyAlgolympBranding = false,
}) => {
  const avatarSrc = echo.imageUrl || defaultEchoImage;
  const rawCategory = echo.category ?? 'Public Echo';
  const firstSlashIndex = rawCategory.indexOf('/');
  const categoryDisplay = (
    firstSlashIndex >= 0 ? rawCategory.slice(0, firstSlashIndex) : rawCategory
  ).trim();

  const usesPurpleStageTheme = theme.name === 'crimson';
  const mobileFadeStyle = usesPurpleStageTheme
    ? {
        background:
          'linear-gradient(0deg, #4750A3 0%, rgba(71, 80, 163, 1) 20%, rgba(71, 80, 163, 0) 100%)',
      }
    : {
        background:
          'linear-gradient(0deg, #2C204C 0%, rgba(44, 32, 76, 0.9) 28%, rgba(44, 32, 76, 0) 100%)',
      };

  return (
    <header className={clsx('relative flex w-full shrink-0 flex-col items-center', 'md:h-full')}>
      {/* Main Container: Mobile auto, Desktop 80% height */}
      <div className='relative flex w-full items-end justify-center p-0 md:h-full md:pb-[111px]'>
        {/* Purple theme grid background - Mobile/Tablet Only */}
        {usesPurpleStageTheme && (
          <img
            src={algolympBg}
            alt=''
            className='pointer-events-none absolute bottom-0 left-0 z-0 w-full object-contain opacity-100 md:hidden'
          />
        )}

        {/* Avatar Image */}
        <img
          src={avatarSrc}
          alt={echo.name}
          className={clsx(
            'relative z-10 object-contain', // z-10: Above algolymp bg
            'size-auto max-h-[280px]', // Mobile: Max height 280px, auto width
            'md:h-full md:max-h-none md:w-auto' // Desktop: Full height, no max-height, auto width
          )}
        />

        {/*
           Gradient Overlay (Bottom-up)
           - Mobile Only (md:hidden)
        */}
        <div
          className='absolute bottom-[-5px] left-0 z-30 h-1/2 w-full md:hidden'
          style={mobileFadeStyle}
        />

        {/* Text Overlay */}
        <div
          className={clsx(
            'absolute bottom-0 left-0 z-30 flex w-full flex-col items-center justify-end',
            'pb-2 md:pb-[72px]' // Mobile: Reduced padding to sit text at bottom edge
          )}
        >
          <div className='flex flex-col items-center text-center text-white'>
            <h2
              className={clsx(
                'font-proxima font-bold leading-105 tracking-normal',
                'text-[22px] md:text-[40px]'
              )}
            >
              {useLegacyAlgolympBranding ? 'Vlad' : echo.name}
            </h2>

            <div className='mt-1 flex items-center gap-2'>
              {useLegacyAlgolympBranding ? (
                <span className='font-proxima text-[22px] font-normal leading-105 md:text-[40px]'>
                  Algolymp Mentor
                </span>
              ) : (
                <>
                  <span className='font-proxima text-[11px] font-bold uppercase leading-tight tracking-normal md:text-[14px]'>
                    D.ECHO
                  </span>
                  <div className={clsx('size-[6px] rounded-full', theme.header.badge)} />
                  <span className='font-sans text-[11px] font-normal leading-tight md:text-[14px]'>
                    {categoryDisplay}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
